if (!customElements.get('tp-bundle-builder')) {
  customElements.define('tp-bundle-builder', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.strings = JSON.parse(this.querySelector('[data-bundle-strings]').textContent);
      this.items = new Map();
      this.busy = false;
      this.morePromise = null;
      this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency || 'EUR' });
      if (matchMedia('(max-width: 767px)').matches) this.querySelector('.tpb-items').open = false;
      this.addEventListener('change', event => this.change(event), { signal });
      this.addEventListener('click', event => this.click(event), { signal });
      this.setupFilter(signal);
      this.setupSummary(signal);
      this.formatCards();
      this.render();
      this.seed().catch(() => this.status(this.strings.error, true));
    }
    disconnectedCallback() { this.controller?.abort(); this.summaryObserver?.disconnect(); this.summaryViewportObserver?.disconnect(); }
    setupSummary(signal) {
      this.summaryObserver?.disconnect();
      const summary = this.querySelector('.tpb-summary');
      const toggle = summary?.querySelector('[data-bundle-summary-toggle]');
      const body = summary?.querySelector('[data-bundle-summary-body]');
      if (!toggle || !body) return;
      const mobile = matchMedia('(max-width: 767px)');
      this.summaryCollapsed ??= true;
      this.syncSummary = () => {
        const collapsed = mobile.matches && this.summaryCollapsed;
        summary.dataset.collapsed = String(collapsed);
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.setAttribute('aria-label', collapsed ? toggle.dataset.expandLabel : toggle.dataset.collapseLabel);
        toggle.title = toggle.getAttribute('aria-label');
        if (collapsed && body.contains(document.activeElement)) toggle.focus({ preventScroll: true });
        body.hidden = collapsed;
      };
      toggle.addEventListener('click', () => {
        if (!mobile.matches) return;
        this.summaryCollapsed = !this.summaryCollapsed;
        this.syncSummary();
      }, { signal });
      mobile.addEventListener('change', this.syncSummary, { signal });
      summary.dataset.summaryReady = '';
      this.syncSummary();
      this.summaryObserver = new ResizeObserver(() => {
        const height = `${Math.ceil(summary.getBoundingClientRect().height)}px`;
        if (this.style.getPropertyValue('--tpb-summary-height') !== height) this.style.setProperty('--tpb-summary-height', height);
      });
      this.summaryObserver.observe(summary);
      this.summaryViewportObserver?.disconnect();
      this.summaryViewportObserver = new IntersectionObserver(entries => {
        summary.toggleAttribute('data-outside-builder', !entries[0].isIntersecting);
      });
      this.summaryViewportObserver.observe(this);
    }
    setupFilter(signal) {
      const select = this.querySelector('[data-bundle-filter]');
      const field = select.closest('.tpb-filter');
      field.querySelector('[data-bundle-filter-picker]')?.remove();
      const label = field.querySelector('label');
      label.id ||= `${select.id}-label`;
      const picker = document.createElement('details');
      picker.className = 'tpb-filter-picker';
      picker.dataset.bundleFilterPicker = '';
      const trigger = document.createElement('summary');
      trigger.className = 'tpb-filter-trigger';
      const value = document.createElement('span');
      value.id = `${select.id}-value`;
      value.textContent = select.selectedOptions[0].textContent;
      trigger.setAttribute('aria-labelledby', `${label.id} ${value.id}`);
      const icon = field.querySelector('.field-select-icon').cloneNode(true);
      icon.className = 'tpb-filter-chevron';
      icon.setAttribute('aria-hidden', 'true');
      trigger.append(value, icon);
      const choices = document.createElement('div');
      choices.className = 'tpb-filter-options';
      choices.setAttribute('role', 'group');
      choices.setAttribute('aria-labelledby', label.id);
      for (const option of select.options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = option.textContent;
        button.disabled = option.disabled;
        button.setAttribute('aria-pressed', String(option.selected));
        button.addEventListener('click', () => {
          if (this.busy) return;
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          picker.open = false;
          trigger.focus({ preventScroll: true });
        }, { signal });
        choices.append(button);
      }
      select.addEventListener('change', () => {
        value.textContent = select.selectedOptions[0].textContent;
        [...choices.children].forEach((button, index) => button.setAttribute('aria-pressed', String(select.options[index].selected)));
      }, { signal });
      picker.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !picker.open) return;
        event.preventDefault();
        picker.open = false;
        trigger.focus({ preventScroll: true });
      }, { signal });
      document.addEventListener('click', event => {
        if (!picker.contains(event.target)) picker.open = false;
      }, { signal });
      picker.append(trigger, choices);
      field.append(picker);
      select.closest('.field-wrap').hidden = true;
    }
    format(value) { return this.money.format(value / 100); }
    text(key, values = {}) {
      let text = this.strings[key] || key;
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`[[${name}]]`, value); });
      return text;
    }
    product(card) { return JSON.parse(card.querySelector('[data-bundle-product]').textContent); }
    formatCards(root = this) {
      const cards = root.matches('[data-bundle-card]') ? [root] : root.querySelectorAll('[data-bundle-card]');
      cards.forEach(card => {
        const product = this.product(card);
        const selected = product.variants.find(v => String(v.id) === card.querySelector('[data-bundle-variant]').value);
        card.querySelector('[data-card-price]').textContent = this.format(selected?.price ?? Math.min(...product.variants.map(v => v.price)));
      });
    }
    status(text, error = false, announceOnly = false) {
      if (error) { this.summaryCollapsed = false; this.syncSummary?.(); }
      const status = this.querySelector('[data-bundle-status]');
      status.textContent = text;
      status.toggleAttribute('data-error', error);
      status.classList.toggle('visually-hidden', announceOnly);
    }
    async change(event) {
      const el = event.target;
      if (this.busy) return;
      if (el.matches('[data-bundle-variant]')) {
        const card = el.closest('[data-bundle-card]');
        const product = this.product(card);
        const variant = product.variants.find(v => String(v.id) === el.value);
        card.querySelector('[data-bundle-add]').disabled = this.busy || !variant?.available;
        if (variant) card.querySelector('[data-card-price]').textContent = this.format(variant.price);
      } else if (el.matches('[data-bundle-color]') && el.value) {
        await this.swapColor(el);
      } else if (el.matches('[data-bundle-filter]')) {
        try {
          this.status(this.strings.loading);
          while (this.querySelector('[data-bundle-more]')) await this.loadMore();
          this.filter();
          this.status('');
        } catch { this.status(this.strings.error, true); }
      } else if (el.matches('[data-item-quantity]')) {
        const item = this.items.get(el.closest('[data-variant-id]').dataset.variantId);
        if (item) item.quantity = Number(el.value);
        this.render();
      }
    }
    async click(event) {
      const button = event.target.closest('[data-bundle-add], [data-item-remove], [data-bundle-submit], [data-load-more]');
      if (!button || this.busy || button.disabled) return;
      event.preventDefault();
      if (button.matches('[data-bundle-add]')) {
        const card = button.closest('[data-bundle-card]');
        const product = this.product(card);
        const id = card.querySelector('[data-bundle-variant]').value;
        const variant = product.variants.find(v => String(v.id) === id && v.available);
        if (!variant) return;
        const existing = this.items.get(id);
        if (existing) existing.quantity += 1;
        else this.items.set(id, { product, variant, quantity: 1 });
        this.render();
        this.status(this.text('added', { product: product.title, size: variant.title }), false, true);
      } else if (button.matches('[data-item-remove]')) {
        this.items.delete(button.closest('[data-variant-id]').dataset.variantId);
        this.render();
        this.status('');
      } else if (button.matches('[data-load-more]')) {
        try { await this.loadMore(); } catch { this.status(this.strings.error, true); }
      } else await this.submit();
    }
    filter() {
      const type = this.querySelector('[data-bundle-filter]').value;
      let visible = 0;
      this.querySelectorAll('[data-bundle-grid] > [data-bundle-card]').forEach(card => {
        card.hidden = type !== 'all' && card.dataset.type !== type;
        if (!card.hidden) visible++;
      });
      this.querySelector('[data-bundle-empty]').hidden = visible > 0;
    }
    async loadMore() {
      if (this.morePromise) return this.morePromise;
      const more = this.querySelector('[data-bundle-more]');
      if (!more) return;
      more.setAttribute('aria-busy', 'true');
      this.morePromise = (async () => {
        const url = new URL(more.dataset.next, location.origin);
        url.searchParams.set('view', 'tp-bundles');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Catalog');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = html.querySelector('[data-bundle-grid]');
        if (!grid) throw new Error('Catalog');
        const current = new Set([...this.querySelectorAll('[data-bundle-card]')].map(c => c.dataset.handle));
        for (const card of grid.children) if (!current.has(card.dataset.handle)) this.querySelector('[data-bundle-grid]').append(card.cloneNode(true));
        const next = html.querySelector('[data-bundle-more]');
        if (next) more.dataset.next = next.dataset.next;
        else more.remove();
        this.formatCards();
        this.filter();
      })();
      try { await this.morePromise; } finally { more.removeAttribute('aria-busy'); this.morePromise = null; }
    }
    async fetchCard(url) {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Product');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('[data-bundle-card]');
      if (!card) throw new Error('Unavailable');
      const suffix = crypto.randomUUID();
      card.querySelectorAll('[id]').forEach(el => {
        const previous = el.id;
        el.id = `${previous}-${suffix}`;
        card.querySelectorAll('label').forEach(label => { if (label.htmlFor === previous) label.htmlFor = el.id; });
      });
      return card;
    }
    async swapColor(select) {
      const card = select.closest('[data-bundle-card]');
      const product = this.product(card);
      const selected = product.variants.find(v => String(v.id) === card.querySelector('[data-bundle-variant]').value);
      card.setAttribute('aria-busy', 'true');
      try {
        const next = await this.fetchCard(select.value);
        const variant = this.product(next).variants.find(v => v.available && v.title === selected?.title);
        if (variant) {
          next.querySelector('[data-bundle-variant]').value = String(variant.id);
          next.querySelector('[data-bundle-add]').disabled = false;
          next.querySelector('[data-card-price]').textContent = this.format(variant.price);
        }
        card.replaceWith(next);
        this.formatCards(next);
        next.querySelector('[data-bundle-variant]:not([type="hidden"]), [data-bundle-add]').focus({ preventScroll: true });
        this.filter();
      } catch { card.removeAttribute('aria-busy'); select.value = ''; this.status(this.strings.error, true); }
    }
    render() {
      const count = [...this.items.values()].reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = [...this.items.values()].reduce((sum, item) => sum + item.quantity * item.variant.price, 0);
      const rate = count >= 5 ? 20 : count >= 3 ? 15 : 0;
      const saving = [...this.items.values()].reduce((sum, item) => sum + Math.round(item.variant.price * rate / 100) * item.quantity, 0);
      this.querySelector('[data-bundle-count]').textContent = `${count} ${count === 1 ? this.strings.garment : this.strings.garments}`;
      this.querySelector('[data-bundle-items-count]').textContent = count;
      this.querySelector('[data-bundle-progress]').value = Math.min(count, 5);
      this.querySelector('[data-bundle-progress-text]').textContent = count >= 5 ? this.text('achieved', { rate: 20 }) : this.text('next', { count: (count < 3 ? 3 : 5) - count, rate: count < 3 ? 15 : 20 });
      this.querySelector('[data-bundle-subtotal]').textContent = this.format(subtotal);
      this.querySelector('[data-bundle-saving]').textContent = `−${this.format(saving)}`;
      this.querySelector('[data-bundle-total]').textContent = this.format(subtotal - saving);
      const compactTotal = this.querySelector('[data-bundle-compact-total]');
      if (compactTotal) compactTotal.textContent = this.format(subtotal - saving);
      this.querySelector('[data-bundle-rate]').textContent = rate ? `(${rate} %)` : '';
      this.querySelector('[data-bundle-submit]').disabled = this.busy || count < 3;
      this.querySelector('[data-bundle-selection-empty]').hidden = count > 0;
      const list = this.querySelector('[data-bundle-items]');
      const template = this.querySelector('[data-bundle-item-template]');
      list.replaceChildren();
      this.items.forEach((item, id) => {
        const node = template.content.firstElementChild.cloneNode(true);
        node.dataset.variantId = id;
        node.querySelector('img').src = item.product.image;
        node.querySelector('img').alt = item.product.title;
        const link = node.querySelector('a');
        link.textContent = item.product.title;
        link.href = `${item.product.url}?variant=${id}`;
        node.querySelector('[data-item-variant]').textContent = item.variant.title;
        const select = node.querySelector('select');
        select.id = `quantity-${this.id}-${id}`;
        node.querySelector('label').htmlFor = select.id;
        node.querySelector('label').textContent = `${this.strings.quantity}: ${item.product.title}, ${item.variant.title}`;
        for (let q = 1; q <= Math.max(20, item.quantity); q++) select.add(new Option(q, q, false, q === item.quantity));
        node.querySelector('button').setAttribute('aria-label', `${this.strings.remove}: ${item.product.title}, ${item.variant.title}`);
        list.append(node);
      });
      this.querySelector('[data-bundle-cart-link]').hidden = true;
    }
    async seed() {
      const params = new URL(location.href).searchParams;
      const handle = params.get('bundle_product');
      const variantId = params.get('bundle_variant');
      if (!handle || !/^[a-z0-9-]+$/.test(handle) || !/^\d+$/.test(variantId || '')) return;
      let card = [...this.querySelectorAll('[data-bundle-card]')].find(c => c.dataset.handle === handle);
      if (!card) {
        const root = this.dataset.root.replace(/\/$/, '');
        card = await this.fetchCard(`${root}/products/${handle}?view=tp-bundle-card`);
        this.querySelector('[data-bundle-grid]').prepend(card);
      }
      const product = this.product(card);
      const variant = product.variants.find(v => String(v.id) === variantId && v.available);
      if (!variant) return;
      card.querySelector('[data-bundle-variant]').value = variantId;
      card.querySelector('[data-bundle-add]').disabled = false;
      this.items.set(variantId, { product, variant, quantity: 1 });
      this.render();
    }
    async submit() {
      if (this.busy) return;
      const items = [...this.items.entries()].map(([id, item]) => ({ id: Number(id), quantity: item.quantity }));
      if (items.reduce((sum, item) => sum + item.quantity, 0) < 3) return;
      this.busy = true;
      const controls = [...this.querySelectorAll('select,button')].map(el => ({ el, disabled: el.disabled }));
      controls.forEach(({ el }) => { el.disabled = true; });
      this.querySelector('[data-bundle-submit]').disabled = true;
      this.status(this.strings.loading);
      let added = false;
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:sections-to-update', { bubbles: true, detail: { sections } }));
        const response = await fetch(this.dataset.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections: [...new Set(sections)].slice(0,5), sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status === 422 || result.errors) throw new Error(typeof result.description === 'string' ? result.description : this.strings.error);
        added = true;
        this.items.clear();
        const root = this.dataset.root.replace(/\/$/, '');
        const cartResponse = await fetch(`${root}/cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(this.strings.success);
        const cart = await cartResponse.json();
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: {}, sourceId: this.id, data: { source: 'product-form', itemCount: cart.item_count, sections: result.sections || {}, openDrawer: true } } }));
        this.status(this.strings.success);
      } catch (error) {
        this.status(added ? this.strings.success : error.message || this.strings.error, !added);
      } finally {
        this.busy = false;
        controls.forEach(({ el, disabled }) => { if (el.isConnected) el.disabled = disabled; });
        this.render();
        this.querySelector('[data-bundle-cart-link]').hidden = false;
      }
    }
  });
}
